/**
 * Handles tabbed focus for siblings of a given element
 *
 * @param callback - Optional callback to trigger when the user tabs to the next or previous sibling
 * @returns (event: KeyboardEvent) => void
 */

import { useCallback } from "react";

interface UseRovingFocusProps {
  onFocusChange?: (element: HTMLElement | null) => void;
  orientation?: "horizontal" | "vertical";
  loop?: boolean;
}

// Keyboard navigation constants
const VERTICAL_KEYS = ["ArrowUp", "ArrowDown"] as const;
const HORIZONTAL_KEYS = ["ArrowRight", "ArrowLeft"] as const;

// Focusable element selectors
const FOCUSABLE_SELECTORS = "button, a, input, textarea, select, details, summary, [tabindex]";

export const useRovingFocus = (props?: UseRovingFocusProps) => {
  const { onFocusChange, orientation = "horizontal", loop = false } = props || {};

  const getFocusableSiblings = useCallback((element: HTMLElement) => {
    const directParent = element.parentElement;

    let parent = directParent?.tagName === "LI" ? directParent.closest("ul") : directParent;

    return Array.from(parent?.querySelectorAll(FOCUSABLE_SELECTORS) || []);
  }, []);

  const handleKeypress = useCallback(
    (event: React.KeyboardEvent) => {
      const key = event.key;

      if (
        (orientation === "horizontal" && HORIZONTAL_KEYS.includes(key as (typeof HORIZONTAL_KEYS)[number])) ||
        (orientation === "vertical" && VERTICAL_KEYS.includes(key as (typeof VERTICAL_KEYS)[number]))
      ) {
        event.preventDefault();
        event.stopPropagation();

        const focusableSiblings = getFocusableSiblings(event.target as HTMLElement);
        const currentIndex = focusableSiblings.indexOf(event.target as HTMLElement);

        if (!focusableSiblings?.length) return;

        if (orientation === "vertical" && VERTICAL_KEYS.includes(key as (typeof VERTICAL_KEYS)[number])) {
          if (
            !loop &&
            ((key === "ArrowUp" && currentIndex === 0) ||
              (key === "ArrowDown" && currentIndex === focusableSiblings.length - 1))
          )
            return;

          const nextIndex =
            key === "ArrowUp"
              ? (currentIndex - 1 + (loop ? focusableSiblings.length : 0)) % focusableSiblings.length
              : key === "ArrowDown"
                ? (currentIndex + 1 + (loop ? focusableSiblings.length : 0)) % focusableSiblings.length
                : -1;

          const nextElement = focusableSiblings[nextIndex] as HTMLElement | null;

          if (!nextElement) return;

          nextElement?.focus();
          onFocusChange?.(nextElement);

          return;
        }

        if (orientation === "horizontal" && HORIZONTAL_KEYS.includes(key as (typeof HORIZONTAL_KEYS)[number])) {
          if (
            !loop &&
            ((key === "ArrowLeft" && currentIndex === 0) ||
              (key === "ArrowRight" && currentIndex === focusableSiblings.length - 1))
          )
            return;

          const nextIndex =
            key === "ArrowLeft"
              ? (currentIndex - 1 + (loop ? focusableSiblings.length : 0)) % focusableSiblings.length
              : key === "ArrowRight"
                ? (currentIndex + 1 + (loop ? focusableSiblings.length : 0)) % focusableSiblings.length
                : -1;
          const nextElement = focusableSiblings[nextIndex] as HTMLElement | null;

          if (!nextElement) return;

          nextElement?.focus();
          onFocusChange?.(nextElement);
        }
      }
    },
    [onFocusChange, orientation, loop],
  );

  return handleKeypress;
};
